// src/components/UserProfile.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/auth";

import { bucketRootRef } from "../helpers/storageCompat";

import {
  BadgeCheck,
  CalendarDays,
  Camera,
  ChevronRight,
  HelpCircle,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
} from "lucide-react";

import EditProfileModal from "./EditProfileModal";
import "./UserProfile.css";

const db = firebase.firestore();

const normStr = (value) => String(value ?? "").trim();
const clampRating = (value) => Math.max(0, Math.min(5, Number(value)));

const dateLabel = (value) => {
  let date = null;

  if (value?.toDate) {
    date = value.toDate();
  } else if (typeof value === "number") {
    date = new Date(value);
  } else if (typeof value === "string") {
    const parsed = new Date(value);
    if (Number.isFinite(parsed.getTime())) date = parsed;
  }

  if (!date || !Number.isFinite(date.getTime())) return "";

  return date.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
  });
};

const normalizePhoneForFirebase = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return "";

  if (raw.startsWith("+")) {
    return raw.replace(/\s+/g, "");
  }

  const digits = raw.replace(/\D/g, "");

  if (!digits) return "";

  if (digits.startsWith("0")) {
    return `+27${digits.slice(1)}`;
  }

  if (digits.startsWith("27")) {
    return `+${digits}`;
  }

  return `+${digits}`;
};

const UserProfile = () => {
  const { currentUser } = useAuth();
  const params = useParams();

  const routeUid = normStr(params?.uid);
  const viewingUid = routeUid || normStr(currentUser?.uid);
  const isSelf = !!currentUser?.uid && viewingUid === currentUser.uid;

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [rating, setRating] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const [verifyMsg, setVerifyMsg] = useState("");
  const [verifyErr, setVerifyErr] = useState("");
  const [isSendingEmailVerification, setIsSendingEmailVerification] =
    useState(false);
  const [isPhoneVerifyOpen, setIsPhoneVerifyOpen] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!viewingUid) {
      setUserData(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const ref = db.collection("users").doc(viewingUid);

    const unsubscribe = ref.onSnapshot(
      (snap) => {
        setUserData(
          snap.exists ? { id: snap.id, ...snap.data() } : { id: viewingUid }
        );
        setLoading(false);
      },
      (error) => {
        console.error("[UserProfile] users/{uid} snapshot error:", error);
        setUserData({ id: viewingUid });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [viewingUid]);

  useEffect(() => {
    if (!viewingUid) return undefined;

    let latestReviewsByToUserId = [];
    let latestReviewsByUserId = [];

    const recomputeRating = () => {
      const merged = new Map();

      latestReviewsByToUserId.forEach((doc) => merged.set(doc.id, doc));
      latestReviewsByUserId.forEach((doc) => merged.set(doc.id, doc));

      let sum = 0;
      let count = 0;

      Array.from(merged.values()).forEach((doc) => {
        const nextRating = Number(doc.data()?.rating);

        if (Number.isFinite(nextRating)) {
          sum += clampRating(nextRating);
          count += 1;
        }
      });

      setReviewCount(count);
      setRating(count ? sum / count : null);
    };

    const unsubscribeReviewsByToUserId = db
      .collection("Reviews")
      .where("toUserId", "==", viewingUid)
      .onSnapshot(
        (snap) => {
          latestReviewsByToUserId = snap.docs;
          recomputeRating();
        },
        (error) => {
          console.warn("[UserProfile] reviews(toUserId) error:", error);
          latestReviewsByToUserId = [];
          recomputeRating();
        }
      );

    const unsubscribeReviewsByUserId = db
      .collection("Reviews")
      .where("userId", "==", viewingUid)
      .onSnapshot(
        (snap) => {
          latestReviewsByUserId = snap.docs;
          recomputeRating();
        },
        (error) => {
          console.warn("[UserProfile] reviews(userId) error:", error);
          latestReviewsByUserId = [];
          recomputeRating();
        }
      );

    return () => {
      try {
        unsubscribeReviewsByToUserId();
      } catch {}
      try {
        unsubscribeReviewsByUserId();
      } catch {}
    };
  }, [viewingUid]);

  const email = useMemo(() => {
    const fromDoc = normStr(
      userData?.email || userData?.emailAddress || userData?.contactEmail
    );
    const fromAuth = isSelf ? normStr(currentUser?.email) : "";
    return fromDoc || fromAuth;
  }, [userData, currentUser, isSelf]);

  const phone = useMemo(() => {
    const fromDoc = normStr(
      userData?.phone || userData?.phoneNumber || userData?.mobile
    );
    const fromAuth = isSelf ? normStr(currentUser?.phoneNumber) : "";
    return fromDoc || fromAuth;
  }, [userData, currentUser, isSelf]);

  const displayName = useMemo(() => {
    const firstAndLast =
      userData?.firstName && userData?.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : "";

    const fromDoc =
      normStr(userData?.displayName) ||
      normStr(userData?.fullName) ||
      normStr(userData?.name) ||
      normStr(firstAndLast);

    const fromAuth = isSelf ? normStr(currentUser?.displayName) : "";

    return fromDoc || fromAuth || "NexterZone Member";
  }, [userData, currentUser, isSelf]);

  const initials = useMemo(() => {
    const parts = displayName.split(/\s+/).filter(Boolean).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase()).join("") || "NZ";
  }, [displayName]);

  const joinedLabel = useMemo(() => {
    const fromDoc = dateLabel(userData?.createdAt || userData?.created_at);

    if (fromDoc) return fromDoc;

    if (isSelf && currentUser?.metadata?.creationTime) {
      return dateLabel(currentUser.metadata.creationTime);
    }

    return "";
  }, [userData, currentUser, isSelf]);

  const locationLabel = useMemo(() => {
    const direct = normStr(
      userData?.locationLabel || userData?.city || userData?.suburb
    );

    if (direct) return direct;

    const loc = userData?.location;

    if (!loc) return "";
    if (typeof loc === "string") return normStr(loc);

    return [loc.suburb, loc.city, loc.region, loc.country]
      .map(normStr)
      .filter(Boolean)
      .join(", ");
  }, [userData]);

  const bioText = useMemo(() => {
    return (
      normStr(userData?.bio) ||
      normStr(userData?.about) ||
      normStr(userData?.profileSummary) ||
      (isSelf
        ? "Complete your profile to help other marketplace users feel more confident when trading with you."
        : "Marketplace member on NexterZone.")
    );
  }, [userData, isSelf]);

  const photoUrlFromDoc = normStr(
    userData?.photoUrl || userData?.avatarUrl || userData?.profilePhotoUrl
  );
  const photoUrlFromAuth = isSelf ? normStr(currentUser?.photoURL) : "";
  const hasCustomAvatar = !!(photoUrlFromDoc || photoUrlFromAuth);

  const avatarUrl = useMemo(() => {
    return (
      photoUrlFromDoc ||
      photoUrlFromAuth ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(
        displayName || initials
      )}&background=0D8ABC&color=fff`
    );
  }, [photoUrlFromDoc, photoUrlFromAuth, displayName, initials]);

  const isEmailVerified = useMemo(() => {
    if (isSelf) return !!currentUser?.emailVerified;
    return !!(userData?.emailVerified || userData?.verifiedEmail);
  }, [isSelf, currentUser, userData]);

  const isPhoneVerified = useMemo(() => {
    if (isSelf && currentUser?.phoneNumber) return true;
    return !!(userData?.phoneVerified || userData?.verifiedPhone);
  }, [isSelf, currentUser, userData]);

  const showPublicContact = !!(
    userData?.publicContact || userData?.showContactInfo
  );
  const canSeeContact = isSelf || showPublicContact;

  const completionItems = useMemo(
    () => [
      { label: "Photo", done: hasCustomAvatar },
      {
        label: "Name",
        done: !!displayName && displayName !== "NexterZone Member",
      },
      { label: "Email", done: !!email },
      { label: "Phone", done: !!phone },
      { label: "Location", done: !!locationLabel },
    ],
    [hasCustomAvatar, displayName, email, phone, locationLabel]
  );

  const completionPct = useMemo(() => {
    const completed = completionItems.filter((item) => item.done).length;
    return Math.round((completed / completionItems.length) * 100);
  }, [completionItems]);

  const trustLabel = useMemo(() => {
    if (rating == null) return "No reviews yet";
    if (rating >= 4.5) return "Highly rated member";
    if (rating >= 4) return "Trusted marketplace member";
    return "Reviewed member";
  }, [rating]);

  const chooseFile = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file || !currentUser || !isSelf) return;

    setUploadErr("");
    setVerifyMsg("");
    setVerifyErr("");
    setIsUploading(true);

    try {
      const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

      if (file.type && !allowedTypes.includes(file.type)) {
        throw new Error("Please upload a JPG, PNG or WEBP image.");
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Please upload an image smaller than 5MB.");
      }

      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `users/${currentUser.uid}/photo/avatar_${Date.now()}.${ext}`;
      const storageRef = bucketRootRef().child(path);

      await storageRef.put(file, {
        contentType: file.type || "image/jpeg",
      });

      const url = await storageRef.getDownloadURL();

      await db.collection("users").doc(currentUser.uid).set(
        {
          photoUrl: url,
          photoUpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );

      setUserData((prev) => ({
        ...(prev || {}),
        photoUrl: url,
      }));
    } catch (error) {
      console.error("[UserProfile] profile image upload failed:", error);
      setUploadErr(error?.message || "Upload failed. Please try again.");
    } finally {
      setIsUploading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSendEmailVerification = async () => {
    if (!currentUser || !isSelf) return;

    setUploadErr("");
    setVerifyMsg("");
    setVerifyErr("");
    setIsSendingEmailVerification(true);

    try {
      await currentUser.reload();

      const refreshedUser = firebase.auth().currentUser;

      if (!refreshedUser?.email) {
        throw new Error("No email address is linked to this account.");
      }

      if (refreshedUser?.emailVerified) {
        await db.collection("users").doc(currentUser.uid).set(
          {
            email: refreshedUser.email,
            emailVerified: true,
            verifiedEmail: true,
            emailVerifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );

        setUserData((prev) => ({
          ...(prev || {}),
          email: refreshedUser.email,
          emailVerified: true,
          verifiedEmail: true,
        }));

        setVerifyMsg("Email already verified.");
        return;
      }

      await refreshedUser.sendEmailVerification({
        url: `${window.location.origin}/profile`,
        handleCodeInApp: false,
      });

      setVerifyMsg(
        "Verification email sent. Open the email link, then return to this page."
      );
    } catch (error) {
      console.error("[UserProfile] email verification failed:", error);
      setVerifyErr(error?.message || "Could not send verification email.");
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  const handlePhoneVerified = ({ phoneNumber }) => {
    setUserData((prev) => ({
      ...(prev || {}),
      phone: phoneNumber,
      phoneNumber,
      phoneVerified: true,
      verifiedPhone: true,
    }));

    setVerifyMsg("Phone number verified successfully.");
    setVerifyErr("");
  };

  if (!currentUser && !routeUid) {
    return <PageLoader label="Loading profile…" />;
  }

  if (loading) {
    return <PageLoader label="Loading profile…" />;
  }

  return (
    <div className="user-profile-page">
      <div className="user-profile-grid">
        <section className="profile-main-card">
          <div className="profile-hero">
            <div className="profile-hero__glow" />

            <div className="profile-hero__content">
              <div className="profile-hero__identity">
                <div className="profile-avatar-wrap">
                  <img
                    src={avatarUrl}
                    alt="Profile avatar"
                    className="profile-avatar"
                  />

                  {isSelf ? (
                    <>
                      <button
                        type="button"
                        onClick={chooseFile}
                        className="profile-avatar-btn"
                        aria-label="Update profile picture"
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <Loader2 size={17} className="spin" />
                        ) : (
                          <Camera size={17} />
                        )}
                      </button>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/*"
                        className="hidden-input"
                        onChange={handleFileChange}
                      />
                    </>
                  ) : null}
                </div>

                <div className="profile-hero__text">
                  <div className="profile-eyebrow">
                    <Sparkles size={13} />
                    Marketplace profile
                  </div>

                  <h1 className="profile-name">{displayName}</h1>

                  <div className="profile-meta-line">
                    {joinedLabel ? (
                      <span>
                        <CalendarDays size={14} />
                        Joined {joinedLabel}
                      </span>
                    ) : null}

                    {locationLabel ? (
                      <span>
                        <MapPin size={14} />
                        {locationLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="profile-trust-pills">
                <TrustPill icon={<ShieldCheck size={14} />} label={trustLabel} />

                {isEmailVerified ? (
                  <TrustPill
                    icon={<BadgeCheck size={14} />}
                    label="Email verified"
                  />
                ) : null}

                {isPhoneVerified ? (
                  <TrustPill
                    icon={<BadgeCheck size={14} />}
                    label="Phone verified"
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="profile-main-body">
            {uploadErr ? <div className="profile-error">{uploadErr}</div> : null}
            {verifyErr ? <div className="profile-error">{verifyErr}</div> : null}
            {verifyMsg ? (
              <div className="profile-success">{verifyMsg}</div>
            ) : null}

            <div className="profile-about-layout">
              <div className="profile-about-card">
                <div className="profile-section-label">
                  <UserRound size={15} />
                  About
                </div>

                <p className="profile-bio">{bioText}</p>

                <div className="profile-info-grid">
                  <ProfileInfoRow
                    icon={<Mail size={17} />}
                    label="Email"
                    value={canSeeContact && email ? email : "Private"}
                    href={canSeeContact && email ? `mailto:${email}` : ""}
                    muted={!canSeeContact || !email}
                  />

                  <ProfileInfoRow
                    icon={<Phone size={17} />}
                    label="Phone"
                    value={canSeeContact && phone ? phone : "Private"}
                    href={
                      canSeeContact && phone
                        ? `tel:${String(phone).replace(/\s+/g, "")}`
                        : ""
                    }
                    muted={!canSeeContact || !phone}
                  />
                </div>
              </div>

              <div className="profile-strength-card">
                <div className="profile-strength-head">
                  <div>
                    <div className="profile-card-title">Profile strength</div>
                    <p>A complete profile helps other users trade with confidence.</p>
                  </div>

                  <div className="profile-strength-score">{completionPct}%</div>
                </div>

                <div className="profile-strength-bar">
                  <span style={{ width: `${completionPct}%` }} />
                </div>

                <div className="profile-checks">
                  {completionItems.map((item) => (
                    <span
                      key={item.label}
                      className={item.done ? "is-done" : "is-pending"}
                    >
                      {item.done ? "✓" : "+"} {item.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside className="profile-side-panel">
          <section className="profile-side-card">
            <div className="profile-side-heading">
              <div>
                <h2>Trust snapshot</h2>
                <p>Signals that help buyers and sellers feel safe.</p>
              </div>

              <div className="profile-side-icon profile-side-icon--star">
                <Star size={20} fill="currentColor" />
              </div>
            </div>

            <div className="profile-rating-card">
              <div className="profile-rating-score">
                {rating == null ? "—" : Number(rating).toFixed(1)}
              </div>

              <div className="profile-rating-label">
                {reviewCount
                  ? `${reviewCount} review${reviewCount === 1 ? "" : "s"}`
                  : "No reviews yet"}
              </div>

              <div className="profile-stars">
                {[0, 1, 2, 3, 4].map((index) => (
                  <Star
                    key={index}
                    size={17}
                    fill={
                      rating != null && index < Math.round(rating)
                        ? "currentColor"
                        : "none"
                    }
                  />
                ))}
              </div>
            </div>

            <div className="profile-trust-list">
              <TrustRow
                label="Email verification"
                ok={isEmailVerified}
                actionLabel={!isEmailVerified && isSelf ? "Verify" : ""}
                onAction={
                  !isEmailVerified && isSelf
                    ? handleSendEmailVerification
                    : undefined
                }
                disabled={isSendingEmailVerification}
              />

              <TrustRow
                label="Phone verification"
                ok={isPhoneVerified}
                actionLabel={!isPhoneVerified && isSelf ? "Verify" : ""}
                onAction={
                  !isPhoneVerified && isSelf
                    ? () => setIsPhoneVerifyOpen(true)
                    : undefined
                }
              />

              <TrustRow label="Member profile" ok={completionPct >= 80} />
            </div>
          </section>

          {isSelf ? (
            <section className="profile-side-card">
              <h2 className="profile-side-title">Quick actions</h2>
              <p className="profile-side-copy">
                Manage your profile and account support from one place.
              </p>

              <div className="profile-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="profile-primary-action"
                >
                  <span>
                    <UserRound size={18} />
                    Edit profile
                  </span>
                  <ChevronRight size={18} />
                </button>

                <ActionLink
                  to="/settings"
                  icon={<Settings size={18} />}
                  label="Settings & Privacy"
                />

                <ActionLink
                  to="/help"
                  icon={<HelpCircle size={18} />}
                  label="Help Centre"
                />
              </div>
            </section>
          ) : (
            <section className="profile-side-card profile-private-card">
              <div className="profile-side-icon profile-side-icon--lock">
                <Lock size={20} />
              </div>

              <div>
                <h2>Protected contact details</h2>
                <p>
                  Contact details are only shown when the member has chosen to make
                  them public. Use listing and offer flows for safer marketplace
                  communication.
                </p>
              </div>
            </section>
          )}
        </aside>
      </div>

      {isSelf ? (
        <EditProfileModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}

      {isSelf ? (
        <PhoneVerificationModal
          isOpen={isPhoneVerifyOpen}
          onClose={() => setIsPhoneVerifyOpen(false)}
          currentUser={currentUser}
          existingPhone={phone}
          onVerified={handlePhoneVerified}
        />
      ) : null}
    </div>
  );
};

const PageLoader = ({ label }) => (
  <div className="profile-loader-page">
    <div className="profile-loader-card">
      <Loader2 size={18} className="spin" />
      <span>{label}</span>
    </div>
  </div>
);

const TrustPill = ({ icon, label }) => (
  <span className="profile-trust-pill">
    {icon}
    {label}
  </span>
);

const ProfileInfoRow = ({ icon, label, value, href, muted }) => {
  const content = (
    <div className={`profile-info-row ${muted ? "is-muted" : ""}`}>
      <div className="profile-info-icon">{icon}</div>

      <div className="profile-info-text">
        <div>{label}</div>
        <span>{value}</span>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} className="profile-info-link">
      {content}
    </a>
  );
};

const TrustRow = ({ label, ok, actionLabel, onAction, disabled }) => (
  <div className="profile-trust-row">
    <span>{label}</span>

    {actionLabel && onAction ? (
      <button
        type="button"
        className="profile-trust-action"
        onClick={onAction}
        disabled={disabled}
      >
        {disabled ? "Sending..." : actionLabel}
      </button>
    ) : (
      <strong className={ok ? "is-complete" : "is-pending"}>
        {ok ? "Complete" : "Pending"}
      </strong>
    )}
  </div>
);

const ActionLink = ({ to, icon, label }) => (
  <Link to={to} className="profile-action-link">
    <span>
      {icon}
      {label}
    </span>
    <ChevronRight size={18} />
  </Link>
);

const PhoneVerificationModal = ({
  isOpen,
  onClose,
  currentUser,
  existingPhone,
  onVerified,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(existingPhone || "");
  const [verificationId, setVerificationId] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState("");
  const [modalMsg, setModalMsg] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPhoneNumber(existingPhone || "");
      setVerificationId("");
      setOtpCode("");
      setModalErr("");
      setModalMsg("");
    }
  }, [isOpen, existingPhone]);

  if (!isOpen) return null;

  const resetRecaptcha = () => {
    try {
      if (window.profilePhoneRecaptcha?.clear) {
        window.profilePhoneRecaptcha.clear();
      }
    } catch (error) {
      console.warn("[UserProfile] recaptcha clear warning:", error);
    }

    window.profilePhoneRecaptcha = null;
  };

  const startVerification = async () => {
    setModalErr("");
    setModalMsg("");
    setBusy(true);

    try {
      const normalizedPhone = normalizePhoneForFirebase(phoneNumber);

      if (!normalizedPhone || normalizedPhone.length < 10) {
        throw new Error("Please enter a valid phone number.");
      }

      resetRecaptcha();

      window.profilePhoneRecaptcha = new firebase.auth.RecaptchaVerifier(
        "profile-phone-recaptcha",
        {
          size: "invisible",
        }
      );

      const provider = new firebase.auth.PhoneAuthProvider();

      const nextVerificationId = await provider.verifyPhoneNumber(
        normalizedPhone,
        window.profilePhoneRecaptcha
      );

      setVerificationId(nextVerificationId);
      setPhoneNumber(normalizedPhone);
      setModalMsg("OTP sent. Please enter the code you received.");
    } catch (error) {
      console.error("[UserProfile] phone OTP send failed:", error);
      resetRecaptcha();
      setModalErr(error?.message || "Could not send OTP.");
    } finally {
      setBusy(false);
    }
  };

  const confirmVerification = async () => {
    setModalErr("");
    setModalMsg("");
    setBusy(true);

    try {
      if (!currentUser?.uid) {
        throw new Error("You must be logged in to verify your phone number.");
      }

      if (!verificationId || !otpCode) {
        throw new Error("Please enter the OTP code.");
      }

      const credential = firebase.auth.PhoneAuthProvider.credential(
        verificationId,
        otpCode.trim()
      );

      try {
        await currentUser.linkWithCredential(credential);
      } catch (linkError) {
        if (
          linkError?.code === "auth/provider-already-linked" ||
          linkError?.code === "auth/phone-number-already-exists"
        ) {
          await currentUser.updatePhoneNumber(credential);
        } else {
          throw linkError;
        }
      }

      const normalizedPhone = normalizePhoneForFirebase(phoneNumber);

      await db.collection("users").doc(currentUser.uid).set(
        {
          phone: normalizedPhone,
          phoneNumber: normalizedPhone,
          phoneVerified: true,
          verifiedPhone: true,
          phoneVerifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );

      onVerified?.({ phoneNumber: normalizedPhone });
      resetRecaptcha();
      onClose();
    } catch (error) {
      console.error("[UserProfile] phone OTP confirmation failed:", error);

      if (error?.code === "auth/credential-already-in-use") {
        setModalErr("This phone number is already linked to another account.");
      } else if (error?.code === "auth/requires-recent-login") {
        setModalErr(
          "Please log out and log in again, then retry phone verification."
        );
      } else if (error?.code === "auth/invalid-verification-code") {
        setModalErr("The OTP code is incorrect. Please check it and try again.");
      } else {
        setModalErr(error?.message || "Could not verify OTP.");
      }
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    resetRecaptcha();
    onClose();
  };

  return (
    <div className="profile-verify-overlay" role="dialog" aria-modal="true">
      <div className="profile-verify-modal">
        <h2>Verify phone number</h2>

        <p>
          Enter your phone number and confirm the OTP sent by SMS. For South
          Africa, you can use 076... or +27...
        </p>

        {modalErr ? <div className="profile-error">{modalErr}</div> : null}
        {modalMsg ? <div className="profile-success">{modalMsg}</div> : null}

        <label className="profile-verify-label">
          Phone number
          <input
            type="tel"
            value={phoneNumber}
            onChange={(event) => setPhoneNumber(event.target.value)}
            placeholder="076 862 1191"
            disabled={busy || !!verificationId}
          />
        </label>

        {verificationId ? (
          <label className="profile-verify-label">
            OTP code
            <input
              type="text"
              value={otpCode}
              onChange={(event) => setOtpCode(event.target.value)}
              placeholder="Enter OTP"
              disabled={busy}
            />
          </label>
        ) : null}

        <div id="profile-phone-recaptcha" />

        <div className="profile-verify-actions">
          <button type="button" onClick={closeModal} disabled={busy}>
            Cancel
          </button>

          {!verificationId ? (
            <button type="button" onClick={startVerification} disabled={busy}>
              {busy ? "Sending..." : "Send OTP"}
            </button>
          ) : (
            <button type="button" onClick={confirmVerification} disabled={busy}>
              {busy ? "Verifying..." : "Verify OTP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;